(() => {
  const motionQuery = '(prefers-reduced-motion: reduce)';
  const focusableSelector = 'a[href], area[href], button, input, select, textarea, iframe, [tabindex], [contenteditable="true"]';

  // Start motion only after visibility is known, including browsers without IO.
  const watchVisibility = (element, callback, signal, threshold = 0.02) => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (signal.aborted) return;
        entries.forEach((entry) => callback(entry.isIntersecting && entry.intersectionRatio >= threshold));
      }, { threshold: [0, threshold] });
      observer.observe(element);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
      return;
    }

    let frame;
    const check = () => {
      frame = null;
      if (signal.aborted) return;
      const rect = element.getBoundingClientRect();
      callback(rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth);
    };
    const schedule = () => { if (frame == null) frame = window.requestAnimationFrame(check); };
    window.addEventListener('scroll', schedule, { passive: true, capture: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    signal.addEventListener('abort', () => window.cancelAnimationFrame(frame), { once: true });
    check();
  };

  if (!customElements.get('om-collection-home')) {
    class OmCollectionHome extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        const { signal } = this.events;
        this.reducedMotion = window.matchMedia(motionQuery);
        this.films = [...this.querySelectorAll('[data-film]')];
        this.visibleFilms = new Set();
        this.pausedFilms = new Set();
        this.manualFilms = new Set();
        this.querySelectorAll('[data-film-toggle]').forEach((button) => {
          button.addEventListener('click', () => {
            const film = button.closest('[data-film]');
            const currentlyPaused = this.pausedFilms.has(film) || (this.reducedMotion.matches && !this.manualFilms.has(film));
            if (currentlyPaused) { this.pausedFilms.delete(film); this.manualFilms.add(film); }
            else { this.pausedFilms.add(film); this.manualFilms.delete(film); }
            this.syncFilms();
          }, { signal });
        });
        this.heroVideos = this.films.flatMap((film) => [...film.querySelectorAll('video')]);
        this.pageActive = true;

        // Prestige may restore controls after a blocked autoplay attempt.
        const hideControls = () => this.heroVideos.forEach((video) => {
          if (video.controls) video.controls = false;
        });
        hideControls();
        if ('MutationObserver' in window) {
          this.controlsObserver = new MutationObserver(hideControls);
          this.heroVideos.forEach((video) => this.controlsObserver.observe(video, { attributes: true, attributeFilter: ['controls'] }));
        }
        this.heroVideos.forEach((video) => video.addEventListener('play', () => this.syncFilms(), { signal }));
        this.films.forEach((film) => watchVisibility(film, (visible) => {
          if (visible) this.visibleFilms.add(film);
          else this.visibleFilms.delete(film);
          this.syncFilms();
        }, signal));
        window.addEventListener('resize', () => this.syncFilms(), { passive: true, signal });
        document.addEventListener('visibilitychange', () => this.syncFilms(), { signal });
        window.addEventListener('pagehide', () => { this.pageActive = false; this.syncFilms(); }, { signal });
        window.addEventListener('pageshow', () => { this.pageActive = true; this.syncFilms(); }, { signal });
        this.reducedMotion.addEventListener('change', () => { this.syncFilms(); this.setupReveals(); }, { signal });
        if ('ResizeObserver' in window) {
          this.filmResizeObserver = new ResizeObserver(() => this.syncFilms());
          this.films.forEach((film) => this.filmResizeObserver.observe(film));
        }
        this.syncFilms();

        this.querySelectorAll('[data-rail-prev], [data-rail-next]').forEach((button) => {
          button.addEventListener('click', () => this.scrollRail(button), { signal });
        });
        this.setupReveals();
      }

      syncFilms() {
        const allowed = this.isConnected && this.pageActive && !document.hidden;
        this.films.forEach((film) => {
          const videos = [...film.querySelectorAll('video')];
          const active = allowed && (!this.reducedMotion.matches || this.manualFilms.has(film)) && !this.pausedFilms.has(film) && this.visibleFilms.has(film) ? videos.find((video) => {
            const rect = video.getBoundingClientRect();
            return video.getClientRects().length > 0 && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && getComputedStyle(video).visibility !== 'hidden';
          }) : null;
          const toggle = film.querySelector('[data-film-toggle]');
          if (toggle) {
            const paused = this.pausedFilms.has(film) || (this.reducedMotion.matches && !this.manualFilms.has(film));
            toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
            toggle.querySelector('[data-film-pause]').hidden = paused;
            toggle.querySelector('[data-film-play]').hidden = !paused;
          }
          videos.forEach((video) => {
            if (video === active) {
              if (video.paused) video.play()?.catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        });
      }

      scrollRail(button) {
        const rails = [...this.querySelectorAll('[data-product-rail]')];
        const target = button.getAttribute('aria-controls');
        let rail = target ? rails.find((item) => item.id === target) : null;
        if (target && !rail) return;
        if (!rail) {
          let group = button.parentElement;
          while (group && this.contains(group)) {
            const candidates = group.querySelectorAll('[data-product-rail]');
            if (candidates.length === 1) { rail = candidates[0]; break; }
            if (group === this || candidates.length > 1) break;
            group = group.parentElement;
          }
        }
        if (!rail) return;
        const card = [...rail.children].find((item) => item.getBoundingClientRect().width > 0);
        if (!card) return;
        const style = getComputedStyle(rail);
        const distance = card.getBoundingClientRect().width + (parseFloat(style.columnGap) || 0);
        const direction = (button.hasAttribute('data-rail-next') ? 1 : -1) * (style.direction === 'rtl' ? -1 : 1);
        rail.scrollBy({ left: distance * direction, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
      }

      setupReveals() {
        this.revealObserver?.disconnect();
        const targets = [...this.querySelectorAll('[data-reveal]')];
        if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
          this.removeAttribute('data-motion-ready');
          targets.forEach((item) => item.classList.add('is-visible'));
          return;
        }
        this.revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.revealObserver.unobserve(entry.target);
          });
        }, { threshold: 0.08 });
        targets.filter((item) => !item.classList.contains('is-visible')).forEach((item) => this.revealObserver.observe(item));
        this.setAttribute('data-motion-ready', '');
      }

      disconnectedCallback() {
        this.events?.abort();
        this.events = null;
        this.controlsObserver?.disconnect();
        this.filmResizeObserver?.disconnect();
        this.revealObserver?.disconnect();
        this.heroVideos?.forEach((video) => video.pause());
        this.removeAttribute('data-motion-ready');
      }
    }
    customElements.define('om-collection-home', OmCollectionHome);
  }

  if (!customElements.get('om-story-slider')) {
    class OmStorySlider extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        const { signal } = this.events;
        const owned = (selector) => [...this.querySelectorAll(selector)].filter((item) => item.closest('om-story-slider') === this);
        this.slides = owned('[data-story-slide]');
        this.dots = owned('[data-story-go]');
        this.controls = owned('[data-story-controls]');
        this.toggle = owned('[data-story-toggle]')[0];
        this.savedTabIndexes = new Map();
        this.decodedImages = new WeakMap();
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.requestedIndex = this.index;
        this.autoIndex = (this.index + 1) % (this.slides.length || 1);
        this.paused = false;
        this.visible = false;
        this.hovering = false;
        this.focused = this.contains(document.activeElement);
        this.pageActive = true;
        this.reducedMotion = window.matchMedia(motionQuery);
        const interval = Number(this.dataset.interval);
        this.interval = Number.isFinite(interval) && interval > 0 ? interval : 8500;
        this.style.setProperty('--om-story-interval', `${this.interval}ms`);

        this.dots.forEach((dot) => {
          const index = Number(dot.dataset.storyGo);
          const valid = Number.isInteger(index) && index >= 0 && index < this.slides.length;
          dot.hidden = !valid;
          if (valid) dot.addEventListener('click', () => this.show(index, true), { signal });
        });
        owned('[data-story-prev]').forEach((button) => button.addEventListener('click', () => this.show(this.requestedIndex - 1, true), { signal }));
        owned('[data-story-next]').forEach((button) => button.addEventListener('click', () => this.show(this.requestedIndex + 1, true), { signal }));
        this.toggle?.addEventListener('click', () => { this.paused = !this.paused; this.sync(); }, { signal });
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') { this.hovering = true; this.sync(); }
        }, { signal });
        this.addEventListener('pointerleave', () => { this.hovering = false; this.sync(); }, { signal });
        this.addEventListener('focusin', () => { this.focused = true; this.sync(); }, { signal });
        this.addEventListener('focusout', () => queueMicrotask(() => {
          if (signal.aborted) return;
          this.focused = this.contains(document.activeElement);
          this.sync();
        }), { signal });
        document.addEventListener('visibilitychange', () => this.sync(), { signal });
        window.addEventListener('pagehide', () => { this.pageActive = false; this.sync(); }, { signal });
        window.addEventListener('pageshow', () => { this.pageActive = true; this.sync(); }, { signal });
        this.reducedMotion.addEventListener('change', () => this.sync(), { signal });
        window.addEventListener('resize', () => {
          // A breakpoint can reveal or hide a companion image while we are waiting.
          this.cancelWarmup();
          if (this.pending) this.show(this.pending.index, this.pending.manual);
          else this.sync();
        }, { passive: true, signal });

        this.activate(this.index);
        this.slides.forEach((slide) => { slide.hidden = false; });
        this.controls.forEach((control) => { control.hidden = this.slides.length < 2; });
        watchVisibility(this, (visible) => { this.visible = visible; this.sync(); }, signal, 0.08);
      }

      show(index, manual = false) {
        if (!this.slides.length) return;
        const target = ((index % this.slides.length) + this.slides.length) % this.slides.length;
        if (manual) this.paused = true;
        this.cancelPending();
        this.cancelWarmup();
        this.requestedIndex = target;
        if (target === this.index) {
          this.autoIndex = (target + 1) % this.slides.length;
          this.sync();
          return;
        }
        const pending = { index: target, manual, controller: new AbortController(), ready: false };
        this.pending = pending;
        this.sync();
        if (this.pending === pending) this.preparePending(pending);
      }

      // Opacity-hidden slides still have layout. Display:none mobile companions do not.
      visibleImages(index) {
        return [...this.slides[index].querySelectorAll('img')].filter((image) => {
          const rect = image.getBoundingClientRect();
          return image.getClientRects().length > 0 && rect.width > 0 && rect.height > 0 && getComputedStyle(image).visibility !== 'hidden';
        });
      }

      imageSource(image) {
        return image.currentSrc || image.src;
      }

      slideReady(index) {
        return this.visibleImages(index).every((image) => image.complete && image.naturalWidth > 0 && this.decodedImages.get(image) === this.imageSource(image));
      }

      prepareImages(index, signal) {
        const controller = new AbortController();
        const cancel = () => controller.abort();
        signal.addEventListener('abort', cancel, { once: true });
        if (signal.aborted) cancel();
        // Keep the current photograph on a slow or failed connection, then unlock.
        const timeout = window.setTimeout(cancel, 6000);
        const images = this.visibleImages(index);
        return Promise.all(images.map((image) => new Promise((resolve) => {
          let settled = false;
          let decoding = false;
          const finish = (ready) => {
            if (settled) return;
            settled = true;
            image.removeEventListener('load', loaded);
            image.removeEventListener('error', failed);
            controller.signal.removeEventListener('abort', aborted);
            resolve(ready);
            if (!ready) cancel();
          };
          const failed = () => finish(false);
          const aborted = () => finish(false);
          const loaded = () => {
            if (decoding || settled) return;
            if (!image.naturalWidth) { finish(false); return; }
            const source = this.imageSource(image);
            if (this.decodedImages.get(image) === source) { finish(true); return; }
            decoding = true;
            Promise.resolve().then(() => image.decode?.()).then(() => {
              if (settled) return;
              const ready = image.complete && image.naturalWidth > 0 && this.imageSource(image) === source;
              if (ready) this.decodedImages.set(image, source);
              finish(ready);
            }, failed);
          };
          if (controller.signal.aborted) { finish(false); return; }
          controller.signal.addEventListener('abort', aborted, { once: true });
          image.addEventListener('load', loaded);
          image.addEventListener('error', failed);
          // Only the requested / next slide's rendered images are promoted from lazy.
          image.loading = 'eager';
          if (image.complete) loaded();
        }))).then((ready) => ready.every(Boolean)).finally(() => {
          window.clearTimeout(timeout);
          signal.removeEventListener('abort', cancel);
          cancel();
        });
      }

      preparePending(pending) {
        pending.ready = false;
        this.prepareImages(pending.index, pending.controller.signal).then((ready) => {
          if (this.pending !== pending || pending.controller.signal.aborted) return;
          if (ready) pending.ready = true;
          else {
            // A broken slide must not trap automatic rotation on the same destination.
            if (!pending.manual) this.autoIndex = (pending.index + 1) % this.slides.length;
            this.cancelPending();
          }
          this.sync();
        });
      }

      cancelPending() {
        this.pending?.controller.abort();
        this.pending = null;
        this.requestedIndex = this.index;
      }

      cancelWarmup() {
        this.warmup?.controller.abort();
        this.warmup = null;
      }

      warmNext() {
        const index = this.autoIndex;
        if (index === this.index || this.warmup?.index === index || window.navigator.connection?.saveData) return;
        this.cancelWarmup();
        const controller = new AbortController();
        this.warmup = { index, controller };
        this.prepareImages(index, controller.signal);
      }

      activate(index) {
        this.index = index;
        this.requestedIndex = index;
        this.autoIndex = (index + 1) % (this.slides.length || 1);
        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === this.index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          slide.inert = !active;
          slide.tabIndex = active ? 0 : -1;
          slide.querySelectorAll(focusableSelector).forEach((item) => {
            if (!active) {
              if (!this.savedTabIndexes.has(item)) this.savedTabIndexes.set(item, item.getAttribute('tabindex'));
              item.setAttribute('tabindex', '-1');
            } else if (this.savedTabIndexes.has(item)) {
              const original = this.savedTabIndexes.get(item);
              if (original === null) item.removeAttribute('tabindex');
              else item.setAttribute('tabindex', original);
              this.savedTabIndexes.delete(item);
            }
          });
        });
        this.dots.forEach((dot) => {
          const active = Number(dot.dataset.storyGo) === this.index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', String(active));
        });
      }

      sync() {
        window.clearTimeout(this.timer);
        this.timer = null;
        const stopped = this.paused || this.reducedMotion.matches;
        const visible = this.isConnected && this.pageActive && this.visible && !document.hidden;
        const canPlay = visible && this.slides.length > 1 && !stopped && !this.hovering && !this.focused;
        if (this.pending && !this.pending.manual && !canPlay) this.cancelPending();
        if (this.pending?.ready && visible) {
          // Recheck after a resize/source change or returning from a background tab.
          if (this.slideReady(this.pending.index)) {
            const index = this.pending.index;
            this.cancelPending();
            this.activate(index);
          } else this.preparePending(this.pending);
        }
        const playing = canPlay && !this.pending;
        this.classList.toggle('is-playing', playing);
        if (this.toggle) {
          this.toggle.hidden = this.reducedMotion.matches || this.slides.length < 2;
          this.toggle.setAttribute('aria-label', stopped ? (this.dataset.playLabel || 'Play slideshow') : (this.dataset.pauseLabel || 'Pause slideshow'));
          const pauseIcon = this.toggle.querySelector('[data-pause-icon]');
          const playIcon = this.toggle.querySelector('[data-play-icon]');
          if (pauseIcon) pauseIcon.hidden = stopped;
          if (playIcon) playIcon.hidden = !stopped;
        }
        if (playing) this.timer = window.setTimeout(() => this.show(this.autoIndex), this.interval);
        if (visible && !stopped && !this.pending && this.slides.length > 1) this.warmNext();
        else this.cancelWarmup();
      }

      disconnectedCallback() {
        window.clearTimeout(this.timer);
        this.timer = null;
        this.cancelPending();
        this.cancelWarmup();
        this.events?.abort();
        this.events = null;
        this.classList.remove('is-playing');
        this.savedTabIndexes?.forEach((original, item) => {
          if (original === null) item.removeAttribute('tabindex');
          else item.setAttribute('tabindex', original);
        });
        this.savedTabIndexes?.clear();
      }
    }
    customElements.define('om-story-slider', OmStorySlider);
  }
})();

  // Arabic-only labels for the two existing Sauce homepage galleries.
  // Their content loads asynchronously and is not exposed to Shopify translations.
  (() => {
    if (!/^ar(?:-|$)/i.test(document.documentElement.lang)) return;
    const watched = new WeakSet();
    const galleries = [
      {
        suffix: 'sauce_instagram_tiktok_ugc_homepage_gallery_cQUrNf',
        labels: [
          ['.snptwdgt__headng', 'Styled by Celebrities', 'إطلالات النجمات'],
          ['.snptwdgt__subheadng', 'our celebrity-inspired wardrobe starts here!', 'اكتشفي إطلالات مستوحاة من النجمات']
        ]
      },
      {
        suffix: 'sauce_instagram_tiktok_ugc_live_feed_V9YURg',
        labels: [
          ['.snptwdgt__headng', 'Find us on Instagram', 'تابعينا على إنستغرام']
        ]
      }
    ];
    const attach = () => {
      galleries.forEach(({ suffix, labels }) => {
        const container = document.querySelector('[id$="' + suffix + '"]');
        if (!container || watched.has(container)) return;
        watched.add(container);
        const translate = () => {
          labels.forEach(([selector, original, arabic]) => {
            container.querySelectorAll(selector).forEach((node) => {
              if (node.textContent.trim() !== original) return;
              node.textContent = arabic;
              node.setAttribute('dir', 'rtl');
              node.style.letterSpacing = 'normal';
            });
          });
        };
        translate();
        if ('MutationObserver' in window) {
          new MutationObserver(translate).observe(container, { childList: true, subtree: true, characterData: true });
        }
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attach, { once: true });
    } else {
      attach();
    }
    document.addEventListener('shopify:section:load', attach);
  })();



